import type { IPublicClientApplication } from '@azure/msal-browser';

/**
 * Idle Timeout Service
 * Logs out users after a period of inactivity
 * Shows a warning modal before automatically logging out
 */
class IdleTimeoutService {
    private msalInstance: IPublicClientApplication | null = null;
    private idleTimeout: NodeJS.Timeout | null = null;
    private warningTimeout: NodeJS.Timeout | null = null;
    private warningCallback: ((secondsRemaining: number) => void) | null = null;
    private logoutCallback: (() => void) | null = null;

    // Configuration (in seconds)
    private readonly IDLE_TIMEOUT = 3600; // 1 hour
    private readonly WARNING_TIME = 300; // Show warning 5 minutes before logout
    private readonly ACTIVITY_CHECK_INTERVAL = 60000; // Check every minute

    private lastActivityTime = Date.now();
    private isMonitoring = false;

    /**
     * Initialize the idle timeout service
     */
    initialize(
        msalInstance: IPublicClientApplication,
        onWarning: (secondsRemaining: number) => void,
        onLogout: () => void
    ) {
        this.msalInstance = msalInstance;
        this.warningCallback = onWarning;
        this.logoutCallback = onLogout;
        this.startMonitoring();
        console.info('[IdleTimeout] Service initialized (1 hour timeout)');
    }

    /**
     * Start monitoring user activity
     */
    private startMonitoring() {
        if (this.isMonitoring) {
            return;
        }

        this.isMonitoring = true;
        this.setupActivityListeners();
        this.setIdleTimers();
    }

    /**
     * Stop monitoring user activity
     */
    stop() {
        this.isMonitoring = false;
        this.removeActivityListeners();
        this.clearTimers();
        console.info('[IdleTimeout] Service stopped');
    }

    /**
     * Setup activity listeners
     */
    private setupActivityListeners() {
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        
        events.forEach(event => {
            document.addEventListener(event, this.handleActivity, true);
        });
    }

    /**
     * Remove activity listeners
     */
    private removeActivityListeners() {
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
        
        events.forEach(event => {
            document.removeEventListener(event, this.handleActivity, true);
        });
    }

    /**
     * Handle user activity - reset idle timer
     */
    private handleActivity = () => {
        const now = Date.now();
        const timeSinceLastActivity = now - this.lastActivityTime;

        // Only process if 30 seconds have passed since last activity
        if (timeSinceLastActivity > 30000) {
            console.debug('[IdleTimeout] User activity detected, resetting timer');
            this.lastActivityTime = now;
            this.setIdleTimers(); // Reset timers
        }
    };

    /**
     * Set idle timers
     */
    private setIdleTimers() {
        this.clearTimers();

        // Warning timer - fires WARNING_TIME seconds before logout
        const warningDelay = (this.IDLE_TIMEOUT - this.WARNING_TIME) * 1000;
        this.warningTimeout = setTimeout(() => {
            this.showWarning();
        }, warningDelay);

        // Logout timer
        const logoutDelay = this.IDLE_TIMEOUT * 1000;
        this.idleTimeout = setTimeout(() => {
            this.performLogout();
        }, logoutDelay);
    }

    /**
     * Show warning before logout
     */
    private showWarning() {
        console.warn(`[IdleTimeout] Inactivity warning - user will be logged out in ${this.WARNING_TIME}s`);
        
        if (this.warningCallback) {
            this.warningCallback(this.WARNING_TIME);
        }
    }

    /**
     * Perform automatic logout
     */
    private performLogout() {
        console.warn('[IdleTimeout] User inactive for 1 hour - logging out automatically');
        
        try {
            this.stop();
            
            if (this.logoutCallback) {
                this.logoutCallback();
            }

            // Also perform MSAL logout
            if (this.msalInstance) {
                this.msalInstance.logoutRedirect({
                    postLogoutRedirectUri: window.location.origin,
                });
            }
        } catch (error) {
            console.error('[IdleTimeout] Error during logout:', error);
        }
    }

    /**
     * Clear all timers
     */
    private clearTimers() {
        if (this.idleTimeout) {
            clearTimeout(this.idleTimeout);
            this.idleTimeout = null;
        }
        if (this.warningTimeout) {
            clearTimeout(this.warningTimeout);
            this.warningTimeout = null;
        }
    }

    /**
     * Extend session (called when user clicks "Stay Logged In")
     */
    extendSession() {
        this.lastActivityTime = Date.now();
        this.setIdleTimers();
        console.info('[IdleTimeout] Session extended by user action');
    }

    /**
     * Get time remaining until logout
     */
    getTimeRemaining(): number {
        const elapsed = (Date.now() - this.lastActivityTime) / 1000;
        const remaining = Math.max(0, this.IDLE_TIMEOUT - elapsed);
        return Math.ceil(remaining);
    }
}

// Export singleton instance
export const idleTimeoutService = new IdleTimeoutService();
